import { z } from "zod";

// Status enum
export const APPLICATION_STATUSES = ["DRAFT", "SUBMITTED", "REVIEWING", "APPROVED", "REJECTED", "WITHDRAWN"] as const;

export const ApplicationStatusSchema = z.enum(APPLICATION_STATUSES).default("DRAFT");
export type ApplicationStatusType = z.infer<typeof ApplicationStatusSchema>;

// Application Data Schema (the form data collected from the apply flow)
export const ApplicationDataSchema = z.object({
  // Eligibility
  authorizedToWork: z.boolean(),
  over18: z.boolean(),

  // Personal Info
  previouslyWorked: z.boolean().nullable(),
  firstName: z.string(),
  middleInitial: z.string().optional(),
  lastName: z.string(),
  preferredName: z.string().optional(),
  email: z.string().email(),
  phone: z.string(),

  // Address
  streetLine1: z.string(),
  streetLine2: z.string().optional(),
  city: z.string(),
  state: z.string().length(2),
  zip: z.string().length(5),

  // Office & Referral
  office: z.string().optional(),
  referralSource: z.string(),
  referralName: z.string().optional(),

  // Physical Requirements (11 items)
  physicalRequirements: z.array(z.boolean()).length(11),

  // Additional Questions
  heatInjury: z.boolean().nullable(),
  hasTransportation: z.boolean().nullable(),
  eventServicesExperience: z.boolean().nullable(),
  hasCellPhone: z.boolean().nullable(),
  hasAdditionalInfo: z.boolean().nullable(),
  additionalInfo: z.string().optional(),
});

export type ApplicationDataType = z.infer<typeof ApplicationDataSchema>;

// MetaSchema for additional metadata
export const ApplicationMetaSchema = z.object({
  submittedAt: z.string().datetime().optional(),
  reviewedAt: z.string().datetime().optional(),
  reviewedBy: z.string().uuid().optional(),
  notes: z.string().optional(),
  source: z.enum(["web", "mobile", "kiosk"]).optional(),
  application_data: ApplicationDataSchema.optional(),
});

export type ApplicationMetaType = z.infer<typeof ApplicationMetaSchema>;

// Main ApplicationType
export type ApplicationType = {
  id: string;
  tenant_id: string;
  status?: ApplicationStatusType;
  meta_data?: ApplicationMetaType;
  created_at?: string | Date;
  // Computed fields for table display (populated by service)
  applicant_name?: string;
  email?: string;
  phone?: string;
};
